/**
 * Wire format for iframe ↔ host postMessage communication.
 *
 * Both the embed loader (`src/embed/embed.ts`) and the iframe-side bridge
 * (`lib/embed-bridge.ts`) import these types, so the contract is enforced at
 * compile time on both sides.
 *
 * Every message carries a `v` field so we can evolve the protocol without
 * breaking already-deployed embed snippets. All messages are tagged with the
 * `reachout:` namespace prefix on `type` so host pages that receive them can
 * ignore unrelated messaging cleanly.
 */

export const PROTOCOL_VERSION = 1;

/** Display modes exposed via the `data-mode` attribute of the embed snippet. */
export type EmbedMode = "floating" | "inline";

/** Messages sent FROM the iframe TO the host page. */
export type IframeToHost =
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:ready";
      /** Chat origin — host should use this for subsequent postMessage targets. */
      chatOrigin: string;
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:resize";
      /** Full content height in px. Use for `inline` mode auto-sizing. */
      height: number;
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:message-sent";
      /** Timestamp at which the student sent a message. No PII. */
      timestamp: string;
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:session-ended";
      reason: "user-closed" | "agent-ended" | "timeout";
    };

/** Messages sent FROM the host page TO the iframe. */
export type HostToIframe =
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:open";
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:close";
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:navigate";
      /** Target route, e.g. "/chat?mode=reachout-ai". */
      to: string;
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:set-user";
      /**
       * Signed Gaggle IdP JWT. When present, the iframe can upgrade from
       * anonymous to authenticated mode. Verified server-side before trust.
       */
      gaggleToken: string;
    }
  | {
      v: typeof PROTOCOL_VERSION;
      type: "reachout:set-theme";
      theme: "light" | "dark";
    };

export function isIframeToHost(data: unknown): data is IframeToHost {
  return (
    typeof data === "object" &&
    data !== null &&
    "v" in data &&
    (data as { v: unknown }).v === PROTOCOL_VERSION &&
    "type" in data &&
    typeof (data as { type: unknown }).type === "string" &&
    (data as { type: string }).type.startsWith("reachout:")
  );
}

export function isHostToIframe(data: unknown): data is HostToIframe {
  return isIframeToHost(data as IframeToHost);
}
